import { MAX_RETURN_PERIOD_DAYS, isValidDayCount } from './return-conditions'
import { ReturnPeriodRule } from './return-period-rules'

// A rule applies when every expression in whens matches; the expressions are kept exactly as stored.
export interface WhenRule {
  whens: string[]
}

export interface Requirement {
  always: boolean
  rules: WhenRule[]
}

export interface KeepYourItemRule extends WhenRule {
  refundPercentage: number
  force: boolean
}

export interface NonReturnableLists {
  productIds: string[]
  categoryIds: string[]
  brandIds: string[]
  collectionIds: string[]
  tagIds: string[]
  orderTagIds: string[]
}

export type ReviewAutoComplete = 'OFF' | 'MISSING' | 'APPROVED'

// Every return condition of a group or a portal. The v2 save replaces them all, so the tabs that edit a
// part of them send the rest back as it was.
export interface GroupReturnConditions {
  // Applies to every purchase date the rules leave out.
  returnPeriodDays: number
  returnPeriodRules: ReturnPeriodRule[]
  maxDaysToSendPackage: number
  reviewFlow: string
  reviewAutoComplete: ReviewAutoComplete
  reviewAutoCompleteDays: number
  startReturnOnPlatformTime: string
  skipFraudDetection: boolean
  paymentsEnabled: boolean
  randomizeReturnReasonsOrder: boolean
  // Stored on each portal, not in the return conditions: saving them never writes it.
  markUnreviewedItemsAsMissing: boolean
  mandatoryReturnComment: boolean
  nonReturnable: NonReturnableLists
  payForLogistics: Requirement
  requireAuthorization: Requirement
  uploadDocuments: Requirement
  keepYourItem: { enabled: boolean; rules: KeepYourItemRule[] }
}

export const MANUAL_REVIEW_FLOW = 'MANUAL'

export const START_RETURN_ON_PLATFORM_TIMES = [
  'START_RETURN_ON_PLATFORM_TIME_UNSPECIFIED',
  'START_RETURN_ON_PLATFORM_TIME_ON_START',
  'START_RETURN_ON_PLATFORM_TIME_ON_ITEM_VERIFICATION',
  'START_RETURN_ON_PLATFORM_TIME_NEVER'
]

const NO_REQUIREMENT: Requirement = { always: false, rules: [] }

export const DEFAULT_GROUP_RETURN_CONDITIONS: GroupReturnConditions = {
  returnPeriodDays: 30,
  returnPeriodRules: [],
  maxDaysToSendPackage: 15,
  reviewFlow: 'AUTO',
  reviewAutoComplete: 'OFF',
  reviewAutoCompleteDays: 0,
  startReturnOnPlatformTime: START_RETURN_ON_PLATFORM_TIMES[0],
  skipFraudDetection: false,
  paymentsEnabled: false,
  randomizeReturnReasonsOrder: false,
  markUnreviewedItemsAsMissing: false,
  mandatoryReturnComment: false,
  nonReturnable: {
    productIds: [],
    categoryIds: [],
    brandIds: [],
    collectionIds: [],
    tagIds: [],
    orderTagIds: []
  },
  payForLogistics: NO_REQUIREMENT,
  requireAuthorization: NO_REQUIREMENT,
  uploadDocuments: NO_REQUIREMENT,
  keepYourItem: { enabled: false, rules: [] }
}

// Mirrors maxReviewAutoCompleteDays in settings-api.
export const MAX_REVIEW_AUTO_COMPLETE_DAYS = 365

export type ConditionsDayField =
  | 'returnPeriodDays'
  | 'maxDaysToSendPackage'
  | 'reviewAutoCompleteDays'

export function invalidDayFields(
  conditions: GroupReturnConditions
): ConditionsDayField[] {
  const invalid: ConditionsDayField[] = []
  if (!isValidDayCount(conditions.returnPeriodDays, MAX_RETURN_PERIOD_DAYS))
    invalid.push('returnPeriodDays')
  // settings-api accepts 0 days to ship, unlike the return window.
  if (
    !Number.isInteger(conditions.maxDaysToSendPackage) ||
    conditions.maxDaysToSendPackage < 0
  )
    invalid.push('maxDaysToSendPackage')
  if (
    conditions.reviewAutoComplete !== 'OFF' &&
    !isValidDayCount(
      conditions.reviewAutoCompleteDays,
      MAX_REVIEW_AUTO_COMPLETE_DAYS
    )
  )
    invalid.push('reviewAutoCompleteDays')
  return invalid
}

// The keep your item rows whose refund percentage is not a number from 0 to 100.
export const invalidRefundPercentages = (conditions: GroupReturnConditions) =>
  conditions.keepYourItem.rules.flatMap((rule, i) =>
    Number.isFinite(rule.refundPercentage) &&
    rule.refundPercentage >= 0 &&
    rule.refundPercentage <= 100
      ? []
      : [i]
  )
